import { Check, X, Clock } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import type { CalendarSlot } from '../../stores/calendarStore';

type JobCategory = 'daily' | 'weekly' | 'monthly' | 'recurring';

const DAILY_JOBS = ['analytics-sync', 'flash-report', 'heartbeat', 'kpi-monitor', 'maintenance', 'memory-health'];
const WEEKLY_JOBS = ['content-calendar', 'holiday-monitor', 'weekly-summary'];
const MONTHLY_JOBS = ['monthly-review', 'token-expiry'];

function getJobCategory(jobId: string): JobCategory {
  if (DAILY_JOBS.includes(jobId)) return 'daily';
  if (WEEKLY_JOBS.includes(jobId)) return 'weekly';
  if (MONTHLY_JOBS.includes(jobId)) return 'monthly';
  return 'recurring';
}

const CATEGORY_STYLES: Record<JobCategory, { bg: string; border: string }> = {
  daily: { bg: 'bg-accent-purple/15', border: 'border-accent-purple' },
  weekly: { bg: 'bg-info/15', border: 'border-info' },
  monthly: { bg: 'bg-warning/15', border: 'border-warning' },
  recurring: { bg: 'bg-accent-purple/15', border: 'border-accent-purple' },
};

const STATUS_ICONS: Record<string, React.ReactNode> = {
  completed: <Check className="h-3 w-3 text-success" />,
  failed: <X className="h-3 w-3 text-error" />,
  missed: <Clock className="h-3 w-3 text-warning" />,
};

interface JobBlockProps {
  slot: CalendarSlot;
}

export function JobBlock({ slot }: JobBlockProps) {
  const category = getJobCategory(slot.job_id);
  const styles = CATEGORY_STYLES[category];
  const statusIcon = STATUS_ICONS[slot.status] ?? null;
  const timeLabel = format(parseISO(slot.scheduled_at), 'h:mm a');

  return (
    <div className={`rounded-md px-2 py-1.5 mb-1 ${styles.bg} border-l-2 ${styles.border}`}>
      <div className="flex items-center justify-between gap-1">
        <span className="text-xs font-medium text-text-primary truncate">
          {slot.job_name}
        </span>
        {statusIcon ? (
          <span className="flex-shrink-0">{statusIcon}</span>
        ) : null}
      </div>
      <span className="text-xs text-text-muted">{timeLabel}</span>
    </div>
  );
}

export { getJobCategory };
export type { JobCategory };
export default JobBlock;
